import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNotification } from "../../../../context/NotificationContext";

const BookingDetailsPopup = ({ bookingId, bookingData, isOpenUpdate, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState({});
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isComplimentary, setIsComplimentary] = useState(false);
  const [taxes, setTaxes] = useState([]); // Store all available taxes from API
  const [applicableTaxes, setApplicableTaxes] = useState({}); // Store taxes applicable per room type
  const [isInitialized, setIsInitialized] = useState(false); // Flag to trigger re-render after initialization
  const modalRef = useRef(null);
  const submitButtonRef = useRef(null);
  const { notify } = useNotification();

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const companyid = localStorage.getItem("companyid") || 61;

  const mealPlanOptions = [
    { value: "EP", label: "EP" },
    { value: "CP", label: "CP" },
    { value: "MAP", label: "MAP" },
    { value: "AP", label: "AP" },
  ];

  // Fetch data sequentially to ensure proper state initialization
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setFormValues({});

        const taxResponse = await axios.get(`${apiUrl}/hmstax/listing`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        let allTaxes = [];
        if (taxResponse.data.success && Array.isArray(taxResponse.data.taxes)) {
          const currentDate = new Date();
          allTaxes = taxResponse.data.taxes
            .filter((tax) => {
              const startDate = new Date(tax.start_date);
              const endDate = tax.end_date ? new Date(tax.end_date) : null;
              return (
                tax.applicable_to === "room_charge" &&
                tax.is_active &&
                tax.tax_type === "percentage" &&
                currentDate >= startDate &&
                (!endDate || currentDate <= endDate)
              );
            })
            .map((tax) => ({
              taxId: tax.tax_id,
              taxName: tax.tax_name === "GST" ? "CGST" : tax.tax_name,
              taxRate: parseFloat(tax.tax_rate),
            }));
          setTaxes(allTaxes);
        }

        const allocationResponse = await axios.get(`${apiUrl}/hmstax/tax_allocation/listing`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        });

        if (allocationResponse.data.success && Array.isArray(allocationResponse.data.allocations)) {
          const rules = allocationResponse.data.allocations.flatMap((allocation) => allocation.rules);
          const taxMap = {};

          rules.forEach((rule) => {
            if (rule.all_room_types) {
              allTaxes.forEach((tax) => {
                if (
                  rule.taxes.includes(tax.taxId) &&
                  !rule.excluded_room_types.includes(bookingData.roomTypeId)
                ) {
                  if (!taxMap[bookingData.roomTypeId]) taxMap[bookingData.roomTypeId] = [];
                  if (!taxMap[bookingData.roomTypeId].some((t) => t.taxId === tax.taxId)) {
                    taxMap[bookingData.roomTypeId].push(tax);
                  }
                }
              });
            } else {
              rule.room_types.forEach((roomTypeId) => {
                if (!taxMap[roomTypeId]) taxMap[roomTypeId] = [];
                rule.taxes.forEach((taxId) => {
                  const tax = allTaxes.find((t) => t.taxId === taxId);
                  if (tax && !taxMap[roomTypeId].some((t) => t.taxId === tax.taxId)) {
                    taxMap[roomTypeId].push(tax);
                  }
                });
              });
            }
          });

          setApplicableTaxes(taxMap);

          const roomsResponse = await axios.get(`${apiUrl}/booking/distinct-assigned-rooms/${bookingId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (roomsResponse.data.success) {
            setAssignedRooms(roomsResponse.data.assignedRooms);
            initializeForm(roomsResponse.data.assignedRooms, taxMap, allTaxes);
            setIsInitialized(true);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setTaxes([]);
        setApplicableTaxes({});
      } finally {
        setLoading(false);
      }
    };

    if (isOpenUpdate && bookingId) {
      fetchData();
    }
  }, [isOpenUpdate, bookingId, apiUrl, token, companyid, bookingData.roomTypeId]);


  const initializeForm = (rooms, taxMap, taxList) => {
    const initialValues = {};
    rooms.forEach((room) => {
      const totalTaxAmount = parseFloat(room.total_tax) || 0;
      const appliedRent = parseFloat(room.room_rate) || 0;
      const roomTypeId = room.room_type_id || bookingData.roomTypeId;
      const applicableRoomTaxes = taxMap[roomTypeId] || taxList;

      let taxDetails = [];
      if (totalTaxAmount > 0 && applicableRoomTaxes.length > 0) {
        const totalTaxRate = applicableRoomTaxes.reduce((sum, tax) => sum + tax.taxRate, 0);
        const baseAmount = totalTaxAmount / (totalTaxRate / 100);
        taxDetails = applicableRoomTaxes.map((tax) => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: Number((baseAmount * (tax.taxRate / 100)).toFixed(2)),
        }));
      } else {
        taxDetails = applicableRoomTaxes.map((tax) => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: 0,
        }));
      }

      initialValues[room.room_number] = {
        occupancy: room.occupancy || "S",
        actualRent: parseFloat(room.actual_price) || parseFloat(room.room_rate) || 0,
        appliedRent: appliedRent,
        taxDetails: taxDetails,
        includeTax: totalTaxAmount > 0,
        mealPlan: mealPlanOptions.find((opt) => opt.value === room.meal_plan) || null,
        effectiveDate: bookingData.checkIn,
      };
    });
    setFormValues(initialValues);
  };
  const calculateTotalAmount = (roomData) => {
    const { appliedRent, taxDetails, includeTax } = roomData || {};
    const totalTaxAmount = includeTax ? taxDetails.reduce((sum, tax) => sum + tax.taxAmount, 0) : 0;
    return (appliedRent || 0) + totalTaxAmount;
  };

  const handleChange = (roomNumber, field, value) => {
    setFormValues((prev) => {
      const updatedRoom = { ...prev[roomNumber] };
      const roomTypeId = assignedRooms.find(r => r.room_number === roomNumber)?.room_type_id || bookingData.roomTypeId;
      const applicableRoomTaxes = applicableTaxes[roomTypeId] || taxes;

      if (field === "appliedRent") {
        updatedRoom[field] = Number(value) || 0;
        if (updatedRoom.includeTax) {
          updatedRoom.taxDetails = applicableRoomTaxes.map((tax) => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: Number((updatedRoom[field] * (tax.taxRate / 100)).toFixed(2)),
          }));
        }
      } else if (field === "includeTax") {
        updatedRoom[field] = value;
        if (value) {
          updatedRoom.taxDetails = applicableRoomTaxes.map((tax) => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: Number((updatedRoom.appliedRent * (tax.taxRate / 100)).toFixed(2)),
          }));
        } else {
          updatedRoom.taxDetails = applicableRoomTaxes.map((tax) => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          }));
        }
      } else {
        updatedRoom[field] = value;
      }
      return { ...prev, [roomNumber]: updatedRoom };
    });
    setErrors((prev) => ({ ...prev, [roomNumber]: { ...prev[roomNumber], [field]: "" } }));
  };

  const handleMakeComplimentary = () => {
    const confirmed = window.confirm("Making this booking complimentary will set all room charges to 0. Proceed?");
    if (confirmed) {
      const updatedFormValues = {};
      Object.keys(formValues).forEach((roomNumber) => {
        const roomTypeId = assignedRooms.find(r => r.room_number === roomNumber)?.room_type_id || bookingData.roomTypeId;
        const applicableRoomTaxes = applicableTaxes[roomTypeId] || taxes;
        updatedFormValues[roomNumber] = {
          ...formValues[roomNumber],
          appliedRent: 0,
          taxDetails: applicableRoomTaxes.map((tax) => ({
            taxName: tax.taxName,
            taxRate: tax.taxRate,
            taxAmount: 0,
          })),
          includeTax: false,
          effectiveDate: formValues[roomNumber].effectiveDate || bookingData.checkIn,
        };
      });
      setFormValues(updatedFormValues);
      setIsComplimentary(true);
      toast.info("Booking marked as complimentary.");
    }
  };

  const validateForm = () => {
    let validationErrors = {};
    assignedRooms.forEach((room) => {
      const roomKey = room.room_number;
      if (formValues[roomKey]?.appliedRent && (isNaN(formValues[roomKey].appliedRent) || formValues[roomKey].appliedRent < 0)) {
        validationErrors[roomKey] = { ...validationErrors[roomKey], appliedRent: "Applied rent must be non-negative" };
      }
      if (formValues[roomKey]?.effectiveDate) {
        const effDate = new Date(formValues[roomKey].effectiveDate);
        const checkIn = new Date(bookingData.checkIn);
        const checkOut = new Date(bookingData.checkOut);
        if (effDate < checkIn || effDate > checkOut) {
          validationErrors[roomKey] = { ...validationErrors[roomKey], effectiveDate: "Effective date must be within stay" };
        }
      }
    });
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("error","Please fix validation errors");
      return;
    }

    const isAllZero = Object.values(formValues).every((room) => calculateTotalAmount(room) === 0);
    if (isComplimentary && isAllZero) {
      const result = await Swal.fire({
        title: "Confirm Complimentary Booking",
        text: "You made this booking complimentary. Want to proceed with the update?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Proceed",
        cancelButtonText: "No, Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        complimentary: isComplimentary,
        roomDetails: assignedRooms.map((room) => {
          const roomData = formValues[room.room_number];
          const totalTaxAmount = roomData.includeTax
            ? roomData.taxDetails.reduce((sum, tax) => sum + tax.taxAmount, 0)
            : 0;
          return {
            roomNumber: room.room_number,
            roomType: room.room_type,
            occupancy: roomData.occupancy,
            appliedRent: roomData.appliedRent,
            taxDetails: roomData.includeTax ? roomData.taxDetails : [],
            includeTax: roomData.includeTax,
            mealPlan: roomData.mealPlan?.value || null,
            effectiveDate: roomData.effectiveDate,
          };
        }),
      };

      const response = await axios.put(`${apiUrl}/booking/update-details`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success","Booking details updated successfully!");
      if (onSubmit) onSubmit(response.data);
      onClose();
      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    } catch (error) {
      notify("error","Failed to update booking details.");
      console.error("Error updating booking details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const isCheckedOutOrCancelledOrNoShow = ["checked-out", "cancelled", "noshow"].includes(bookingData?.bookingStatus);
  const disableFields = isCheckedOutOrCancelledOrNoShow;

  return (
    <div ref={modalRef} id="booking_details_popup" className="modal custom-modal fade" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "60%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header" style={{ backgroundColor: "#f0f8ff", borderBottom: "1px solid #e0e7ff" }}>
              <h5 className="modal-title" style={{ fontWeight: "600", color: "#333" }}>
                Booking Details
              </h5>
              <button type="button" className="btn-close" onClick={handleCancel} />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
              <div className="row g-2">
                <div className="col-12 mb-2">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <h6 className="card-title mb-2">Guest Details</h6>
                    <div className="row g-0">
                      <div className="col-6 pe-2">
                        <p><strong>Folio No:</strong> {bookingData?.bookingNumber || "N/A"}</p>
                        <p>
                          <strong>Arrival:</strong>{" "}
                          {new Date(bookingData?.checkIn).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                        <p>
                          <strong>Departure:</strong>{" "}
                          {new Date(bookingData?.checkOut).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }) || "N/A"}
                        </p>
                      </div>
                      <div className="col-6 ps-2">
                        <p><strong>Status:</strong> {bookingData?.bookingStatus || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="card" style={{ border: "1px solid #e0e7ff", padding: "10px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="card-title mb-0">Room Details</h6>
                      <button
                        type="button"
                        className="btn btn-outline-warning btn-sm"
                        onClick={handleMakeComplimentary}
                        disabled={disableFields}
                      >
                        Make Complimentary
                      </button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr style={{ backgroundColor: "#e0e7ff" }}>
                            <th>Room Type</th>
                            <th>Room Number</th>
                            <th>Occupancy</th>
                            <th>Actual Rent</th>
                            <th>Tax Breakdown</th>
                            <th>Applied Rent</th>
                            <th>Total Amount</th>
                            <th>Meal Plan</th>
                            <th>Effective Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedRooms.map((room, index) => {
                            const roomKey = room.room_number;
                            const totalAmount = calculateTotalAmount(formValues[roomKey] || {});
                            return (
                              <tr key={index}>
                                <td>{room.room_type}</td>
                                <td>{room.room_number}</td>
                                <td>
                                  <Select
                                    value={{ value: formValues[roomKey]?.occupancy, label: formValues[roomKey]?.occupancy }}
                                    onChange={(option) => handleChange(roomKey, "occupancy", option.value)}
                                    options={[
                                      { value: "S", label: "Single" },
                                      { value: "D", label: "Double" },
                                    ]}
                                    isDisabled={disableFields}
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                      }),
                                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    }}
                                  />
                                </td>
                                <td>{formValues[roomKey]?.actualRent.toFixed(2)}</td>
                                <td>
                                  {formValues[roomKey]?.taxDetails?.map((tax, idx) => (
                                    <div key={idx}>
                                      {tax.taxName} ({tax.taxRate}%): {tax.taxAmount.toFixed(2)}
                                    </div>
                                  ))}
                                  <div>
                                    <label className="switch" style={{ marginTop: "5px" }}>
                                      <input
                                        type="checkbox"
                                        checked={formValues[roomKey]?.includeTax || false}
                                        onChange={(e) => handleChange(roomKey, "includeTax", e.target.checked)}
                                        disabled={disableFields || isComplimentary}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    value={formValues[roomKey]?.appliedRent || 0}
                                    onChange={(e) => handleChange(roomKey, "appliedRent", e.target.value)}
                                    disabled={disableFields}
                                    min="0"
                                    step="0.01"
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      width: "100px",
                                    }}
                                  />
                                  {errors[roomKey]?.appliedRent && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[roomKey].appliedRent}
                                    </div>
                                  )}
                                </td>
                                <td>{totalAmount.toFixed(2)}</td>
                                <td>
                                  <Select
                                    value={formValues[roomKey]?.mealPlan || null}
                                    onChange={(option) => handleChange(roomKey, "mealPlan", option)}
                                    options={mealPlanOptions}
                                    isClearable
                                    placeholder="Select Meal Plan"
                                    isDisabled={disableFields}
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                      }),
                                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    }}
                                  />
                                </td>
                                <td>
                                  <DatePicker
                                    selected={formValues[roomKey]?.effectiveDate ? new Date(formValues[roomKey].effectiveDate) : null}
                                    onChange={(date) => handleChange(roomKey, "effectiveDate", date ? date.toISOString().split("T")[0] : null)}
                                    minDate={new Date(bookingData.checkIn)}
                                    maxDate={new Date(bookingData.checkOut)}
                                    dateFormat="yyyy-MM-dd"
                                    disabled={disableFields}
                                    className="form-control"
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      width: "150px",
                                    }}
                                  />
                                  {errors[roomKey]?.effectiveDate && (
                                    <div className="invalid-feedback d-block" style={{ color: "#dc3545" }}>
                                      {errors[roomKey].effectiveDate}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ backgroundColor: "#f0f8ff" }}>
              <button type="submit" className="btn btn-primary" disabled={loading || disableFields}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
            <div style={{ display: "none" }}>
              <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                Submit2
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingDetailsPopup;